import { NfcTagRequestStatus } from "@clipboard-health/contract-backend-main";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { Text } from "@src/lib/ui-react";
import { useState } from "react";

import { type NfcTag, type SaveNfcTagLocationParams } from "../types";
import { NfcTagRow } from "./NfcTagRow";

const MAX_NFC_TAGS = 10;

interface NfcTagConfigProps {
  nfcRequestList: NfcTag[];
  isMethodEnabled: boolean;
  onSaveLocation?: (params: SaveNfcTagLocationParams) => Promise<void>;
  onDeleteTag?: (tagId: string) => Promise<void>;
  onRequestTags?: (numberOfTags: number) => Promise<void>;
}

interface RequestTagsSectionProps {
  availableTagsToRequest: number;
  onRequestTags?: (numberOfTags: number) => Promise<void>;
}

function RequestTagsSection(props: RequestTagsSectionProps) {
  const { availableTagsToRequest, onRequestTags } = props;
  const [selectedCount, setSelectedCount] = useState(1);
  const [isRequesting, setIsRequesting] = useState(false);

  if (availableTagsToRequest <= 0 || !onRequestTags) {
    return null;
  }

  async function handleRequestTags() {
    if (!onRequestTags) {
      return;
    }
    setIsRequesting(true);
    try {
      await onRequestTags(selectedCount);
    } finally {
      setIsRequesting(false);
    }
  }

  const options = Array.from({ length: availableTagsToRequest }, (_, index) => index + 1);

  return (
    <Stack spacing={1}>
      <Text variant="body2" color="text.secondary">
        Clipboard time-clock tags are placed on posters. In order to clock in and out, professionals
        will need to tap their mobile devices against the posters, which ensures greater timekeeping
        accuracy. You can request a maximum of {MAX_NFC_TAGS} posters.
      </Text>
      <Stack direction="row" alignItems="center" spacing={2}>
        <FormControl size="small">
          <Select
            value={selectedCount}
            disabled={isRequesting}
            onChange={(event) => {
              setSelectedCount(Number(event.target.value));
            }}
          >
            {options.map((number) => (
              <MenuItem key={number} value={number}>
                {number}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          disabled={isRequesting}
          onClick={async () => {
            await handleRequestTags();
          }}
        >
          {isRequesting ? "Requesting..." : "Request tags"}
        </Button>
      </Stack>
    </Stack>
  );
}

export function NfcTagConfig(props: NfcTagConfigProps) {
  const { nfcRequestList, isMethodEnabled, onSaveLocation, onDeleteTag, onRequestTags } = props;
  const isMobile = useIsMobile();

  const availableTagsToRequest = MAX_NFC_TAGS - nfcRequestList.length;
  const hasTags = nfcRequestList.length > 0;
  const hasPendingTags = nfcRequestList.some(
    (tag) => tag.tagStatus === NfcTagRequestStatus.REQUESTED
  );
  const hasMultipleTags = nfcRequestList.length > 1;
  const setUpTagCount = nfcRequestList.filter(
    (tag) => tag.tagStatus === NfcTagRequestStatus.SET_UP
  ).length;
  const isLastSetUpTag = isMethodEnabled && setUpTagCount === 1;

  return (
    <Stack spacing={3}>
      {hasTags && (
        <Stack spacing={2}>
          <Text variant="body1">Active tags</Text>

          {isMobile ? (
            <Stack>
              {nfcRequestList.map((tag) => {
                const isSetUpTag = tag.tagStatus === NfcTagRequestStatus.SET_UP;
                const canDelete = !(isLastSetUpTag && isSetUpTag);
                return (
                  <NfcTagRow
                    key={tag.tagId}
                    tag={tag}
                    canDelete={canDelete}
                    deleteDisabledReason={
                      !canDelete
                        ? "Cannot delete the only Set up tag while the NFC method is enabled. Disable the method first."
                        : undefined
                    }
                    onSaveLocation={onSaveLocation}
                    onDeleteTag={onDeleteTag}
                  />
                );
              })}
            </Stack>
          ) : (
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Tag #</TableCell>
                  <TableCell sx={{ width: 150 }}>Status</TableCell>
                  <TableCell>Tag Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nfcRequestList.map((tag) => {
                  const isSetUpTag = tag.tagStatus === NfcTagRequestStatus.SET_UP;
                  const canDelete = !(isLastSetUpTag && isSetUpTag);
                  return (
                    <NfcTagRow
                      key={tag.tagId}
                      tag={tag}
                      canDelete={canDelete}
                      deleteDisabledReason={
                        !canDelete
                          ? "Cannot delete the only Set up tag while the NFC method is enabled. Disable the method first."
                          : undefined
                      }
                      onSaveLocation={onSaveLocation}
                      onDeleteTag={onDeleteTag}
                    />
                  );
                })}
              </TableBody>
            </Table>
          )}

          {(hasPendingTags || hasMultipleTags) && (
            <Text variant="body2" color="text.secondary">
              {hasPendingTags &&
                'Clipboard Health admins will change the status to "On Its Way" once the tag is in the mail. '}
              {hasMultipleTags && "Please note that tags are interchangeable."}
            </Text>
          )}
        </Stack>
      )}

      <RequestTagsSection
        availableTagsToRequest={availableTagsToRequest}
        onRequestTags={onRequestTags}
      />
    </Stack>
  );
}
